import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Lozenge from '@atlaskit/lozenge';
import { LinkMenuItem } from '@atlaskit/navigation-system/side-nav-items/link-menu-item';

import { PageStarButton } from '@confluence/action-buttons/entry-points/pageStar';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context';
import {
	getEmojiPublishedFromProperties,
	getEmojiDraftFromProperties,
} from '@confluence/emoji-title/entry-points/transformer';
import {
	getEmojiFromTitle,
	removeEmojiFromTitle,
} from '@confluence/emoji-title/entry-points/helpers';
import { ContentIcon } from '@confluence/icons/entry-points/ContentIcon';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback';
import { fg } from '@confluence/feature-gating';
import { useFlyoutActions } from '@confluence/flyout-store';

import type { ContentMenuFragment as Content } from './queries/__types__/ContentMenuFragment';

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ './analyticsCallbacks'))
			.fireContentMenuItemClickedAnalytics,
);

type ContentMenuItemProps = {
	content: Content;
	actionSubjectId: string;
	url?: string;
	section?: string;
	selectedFilter?: string;
	timestamp?: string;
	hideStarButton?: boolean;
};

export const ContentMenuItem = ({
	content,
	actionSubjectId,
	url,
	section,
	selectedFilter,
	timestamp,
	hideStarButton,
}: ContentMenuItemProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { id, type, links, space, metadata } = content;
	const { closeFlyout } = useFlyoutActions();

	const fireClickItemAnalytics = useLazyClickAnalytics(createAnalyticsEvent, actionSubjectId, {
		componentType: 'customItem',
		contentType: type,
		contentId: id,
		contentStatus: content?.status,
		section,
		selectedFilter,
	});

	const fireStarClickAnalytics = useLazyClickAnalytics(createAnalyticsEvent, actionSubjectId, {
		componentType: 'customItemAction',
		itemId: 'star',
		parentContentType: type,
		parentContentId: id,
	});

	const fireUnstarClickAnalytics = useLazyClickAnalytics(createAnalyticsEvent, actionSubjectId, {
		componentType: 'customItemAction',
		itemId: 'unstar',
		parentContentType: type,
		parentContentId: id,
	});

	const onClick = () => {
		closeFlyout();
		void fireClickItemAnalytics();
	};

	if (!id) {
		return null;
	}

	const isStarred = metadata?.currentuser?.favourited?.isFavourite || false;
	const description = timestamp ? `${space?.name} · ${timestamp}` : space?.name;
	const link = url || `${CONTEXT_PATH}${links?.webui}`;

	const isEmojiContentType =
		content?.type &&
		(content?.type != 'whiteboard' || fg('confluence_frontend_whiteboard_emoji_titles'));
	const { emoji = undefined, hasEmojiProperty = undefined } = isEmojiContentType
		? getEmoji(content)
		: {};
	const title = isEmojiContentType ? getTitle(content?.title, hasEmojiProperty) : content?.title;
	const isDraft = content?.status === 'draft';

	const starButton = (
		<PageStarButton
			contentId={id}
			isStarred={isStarred}
			size="small"
			tooltipPosition="top"
			onStar={fireStarClickAnalytics}
			onUnstar={fireUnstarClickAnalytics}
			spacing="none"
		/>
	);

	return (
		<LinkMenuItem
			elemBefore={type && <ContentIcon type={type} emoji={emoji} subType={content?.subType} />}
			elemAfter={
				isDraft && (
					<Lozenge appearance="default">
						<FormattedMessage {...i18n.draftLozengeText} />
					</Lozenge>
				)
			}
			actions={!hideStarButton && isStarred ? starButton : undefined}
			actionsOnHover={!hideStarButton && !isStarred ? starButton : undefined}
			href={link}
			description={description || ''}
			onClick={onClick}
		>
			{title || <FormattedMessage {...i18n.untitled} />}
		</LinkMenuItem>
	);
};

const getEmoji = (content: Content) => {
	const emojiPropertyPublished = getEmojiPublishedFromProperties(content);
	const emojiPropertyDraft = getEmojiDraftFromProperties(content);

	const emoji = emojiPropertyPublished || emojiPropertyDraft || getEmojiFromTitle(content.title);

	return {
		hasEmojiProperty: Boolean(emojiPropertyPublished || emojiPropertyDraft),
		emoji,
	};
};

const getTitle = (
	originalTitle: string | null | undefined,
	hasEmojiProperty: boolean | undefined,
) => (hasEmojiProperty ? originalTitle : removeEmojiFromTitle(originalTitle));

const i18n = defineMessages({
	untitled: {
		id: 'side-navigation.content-menu.untitled',
		defaultMessage: 'Untitled',
		description: 'Title shown in global navigation content menu when the content has no title',
	},
	draftLozengeText: {
		id: 'side-navigation.content-menu.lozenge.draft',
		defaultMessage: 'DRAFT',
		description: 'Lozenge shown in global navigation content menu when the content is a draft',
	},
});
